import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { FaSpinner } from "react-icons/fa";
import { useDeployAuth } from "../../context/DeployAuthContext";
import LimeLogo from "../../assets/lime-logo-no-bg.png";
import CapacityNotice from "../../components/limelite/CapacityNotice";
import { selectedProject, useLimeliteFunnel } from "../../lib/limeliteFunnel";

/**
 * Register / login for RivieraDeploy dashboard (RD-0047).
 */
function DeployLoginPage() {
  console.log(`[${new Date().toISOString()}] [DeployLoginPage] render`);
  const {
    login,
    register,
    isLoading: sessionLoading,
    error,
    setError,
    capacityMessage,
    setCapacityMessage,
    deploySupportEmail,
    isAuthenticated,
  } = useDeployAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const inviteToken = new URLSearchParams(location.search).get("invite") || "";
  const params = new URLSearchParams(location.search);
  const projectType = selectedProject(params.get("project"));
  const track = useLimeliteFunnel();
  const startedRef = useRef(false);
  const [mode, setMode] = useState(inviteToken || params.get("mode") === "register" ? "register" : "login");
  const [email, setEmail] = useState(new URLSearchParams(location.search).get("email") || "");
  const [password, setPassword] = useState("");
  const [company, setCompany] = useState("");
  const [legalAccepted, setLegalAccepted] = useState(false);
  const [legalDocuments, setLegalDocuments] = useState([]);
  const [legalLoading, setLegalLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const from = location.state?.from?.pathname || `/limelite/deploy/dashboard${projectType ? `?project=${projectType}` : ""}`;
  const justVerified = new URLSearchParams(location.search).get("email_verified") === "1";

  useEffect(() => {
    console.log(`[${new Date().toISOString()}] [DeployLoginPage] mount`);
  }, []);

  useEffect(() => {
    let active = true;
    axios.get("/api/deploy/legal/current-documents").then(({ data }) => {
      if (active && data?.success && Array.isArray(data.documents)) setLegalDocuments(data.documents);
    }).catch(() => { if (active) setLegalDocuments([]); }).finally(() => { if (active) setLegalLoading(false); });
    return () => { active = false; };
  }, []);

  const legalDocument = (type) => legalDocuments.find((document) => document.documentType === type);
  const termsDocument = legalDocument("terms_of_service");
  const privacyDocument = legalDocument("privacy_policy");

  useEffect(() => {
    if (!sessionLoading && isAuthenticated) {
      console.log(`[${new Date().toISOString()}] [DeployLoginPage] already authenticated → redirect`);
      navigate(from, { replace: true });
    }
  }, [sessionLoading, isAuthenticated, navigate, from]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setCapacityMessage("");
    setBusy(true);
    const t0 = Date.now();
    try {
      let ok = false;
      if (mode === "login") {
        ok = await login(email, password);
      } else {
        ok = await register({ email, password, company, termsAccepted: legalAccepted, privacyAccepted: legalAccepted, inviteToken });
      }
      console.log(
        `[${new Date().toISOString()}] [DeployLoginPage] submit done ms=${Date.now() - t0} ok=${ok}`
      );
      if (ok) {
        if (mode === "register") track("signup_completed", { source: "login", projectType });
        navigate(from, { replace: true });
      }
    } finally {
      setBusy(false);
    }
  };

  if (sessionLoading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center bg-black text-silver-light">
        <FaSpinner className="animate-spin text-2xl" />
      </div>
    );
  }

  return (
    <div className="bg-black min-h-[calc(100vh-4rem)] flex items-center justify-center font-sans p-4">
      <div className="bg-off-black rounded-lg p-8 border border-gray-800 w-full max-w-md shadow-xl">
        {justVerified && (
          <div className="mb-5 flex items-center gap-2 rounded-lg border border-lime-500/30 bg-lime-500/10 px-4 py-3 text-sm text-lime-400">
            <span className="text-base">✓</span>
            <span><strong>Email verified!</strong> Sign in below to access your dashboard.</span>
          </div>
        )}
        {/* Visible brand is Limelite — one €29 platform, not a separate Deploy SKU. */}
        <div className="flex items-center justify-center gap-2 mb-1">
          <img src={LimeLogo} alt="Limelite" className="w-7 h-7" />
          <span className="text-xs uppercase tracking-widest text-lime-500">Limelite</span>
        </div>
        <h1 className="text-2xl font-serif font-medium text-center mb-1 text-silver-light">
          {mode === "login" ? "Welcome back" : "Create your free account"}
        </h1>
        <p className="text-center text-silver-dark text-sm mb-6">
          {mode === "login" ? "Sign in to your dashboard" : inviteToken ? "Accept your invitation and connect your AI assistant." : "Get your MCP connection command and explore projects. No card required."}
        </p>

        <div className="flex rounded-md border border-gray-700 mb-6 overflow-hidden">
          <button
            type="button"
            className={`flex-1 py-2 text-sm font-medium ${
              mode === "login" ? "bg-gray-800 text-silver-light" : "text-silver-medium hover:text-silver-light"
            }`}
            onClick={() => setMode("login")}
          >
            Sign in
          </button>
          <button
            type="button"
            className={`flex-1 py-2 text-sm font-medium ${
              mode === "register" ? "bg-gray-800 text-silver-light" : "text-silver-medium hover:text-silver-light"
            }`}
            onClick={() => setMode("register")}
          >
            {inviteToken ? "Accept invitation" : "Start free"}
          </button>
        </div>

        {mode === "register" && <p className="mb-6 text-sm text-silver-medium">Connecting and exploring are free. {projectType === "own_repo" ? "Launching your own repository requires €29/month membership, with €15 monthly infrastructure allowance." : "Your first Ecommerce project costs €49/month including membership, with €25 monthly infrastructure allowance."} Usage above the allowance is billed separately. <Link to="/limelite/projects" className="text-lime-400 underline">See plans and usage charges</Link></p>}

        <form onSubmit={handleSubmit} className="space-y-5" onFocus={() => { if (mode === "register" && !startedRef.current) { startedRef.current = true; track("signup_started", { source: "login", projectType }); } }}>
          <div>
            <label className="block text-sm font-medium mb-1 text-silver-medium" htmlFor="deploy-email">
              Email
            </label>
            <input
              id="deploy-email"
              className="bg-black border rounded-md w-full py-2 px-3 text-silver-light leading-tight focus:outline-none focus:ring-1 focus:ring-silver-light border-gray-700"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
          </div>

          {mode === "register" && termsDocument && privacyDocument && (
            <label className="flex items-start gap-2 text-xs leading-5 text-silver-medium">
              <input type="checkbox" className="mt-1" checked={legalAccepted} onChange={(e) => setLegalAccepted(e.target.checked)} required />
              <span>
                I agree to the <a className="underline hover:text-silver-light" href={termsDocument.documentUrl} target="_blank" rel="noreferrer">Terms of Service ({termsDocument.documentVersion})</a> and
                {" "}<a className="underline hover:text-silver-light" href={privacyDocument.documentUrl} target="_blank" rel="noreferrer">Privacy Policy ({privacyDocument.documentVersion})</a>.
              </span>
            </label>
          )}
          {mode === "register" && (!termsDocument || !privacyDocument) && <p role="status" className="text-sm text-silver-medium">{legalLoading ? "Loading terms and privacy policy…" : <>We could not load the signup terms. <button type="button" className="text-lime-400 underline" onClick={() => window.location.reload()}>Reload and try again</button></>}</p>}

          {mode === "register" && (
            <div>
              <label className="block text-sm font-medium mb-1 text-silver-medium" htmlFor="deploy-company">
                Company (optional)
              </label>
              <input
                id="deploy-company"
                className="bg-black border rounded-md w-full py-2 px-3 text-silver-light leading-tight focus:outline-none focus:ring-1 focus:ring-silver-light border-gray-700"
                type="text"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                autoComplete="organization"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium mb-1 text-silver-medium" htmlFor="deploy-password">
              Password
            </label>
            <input
              id="deploy-password"
              className="bg-black border rounded-md w-full py-2 px-3 text-silver-light leading-tight focus:outline-none focus:ring-1 focus:ring-silver-light border-gray-700"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={mode === "register" ? 10 : 1}
              autoComplete={mode === "login" ? "current-password" : "new-password"}
            />
            {mode === "register" && (
              <p className="text-xs text-silver-medium mt-1">At least 10 characters.</p>
            )}
            {mode === "login" && (
              <p className="text-right mt-1">
                <Link to="/limelite/deploy/forgot-password" className="text-xs text-silver-medium underline hover:text-silver-light">
                  Forgot password?
                </Link>
              </p>
            )}
          </div>

          {error && <p className="text-red-400 text-sm text-center">{error}</p>}

          {capacityMessage && <CapacityNotice message={capacityMessage} contactEmail={deploySupportEmail} />}

          <button
            type="submit"
            disabled={busy || Boolean(capacityMessage) || (mode === "register" && (!termsDocument || !privacyDocument || !legalAccepted))}
            className="w-full bg-lime-600 hover:bg-lime-500 text-black font-bold py-2.5 px-6 rounded-md flex items-center justify-center disabled:opacity-50 transition-colors"
          >
            {busy ? <FaSpinner className="animate-spin mr-2" /> : null}
            {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create free account"}
          </button>
        </form>

        <p className="text-center mt-6 text-sm text-silver-dark">
          <Link to="/limelite" className="hover:text-silver-medium transition-colors">
            ← Back to Limelite
          </Link>
        </p>
      </div>
    </div>
  );
}

export default DeployLoginPage;
